// 数据服务 - 基于 Supabase 云端数据
import supabaseCategoryService from './supabaseCategoryService.js'
import supabaseTagService from './supabaseTagService.js'
import supabaseArticleService from './supabaseArticleService.js'

/**
 * 获取文章列表 - 优先使用本地数据库，后台同步
 * @returns {Promise<Array>} 文章列表
 */
export async function getArticles() {
    try {
        // 直接从 Supabase 获取公开文章列表
        return await supabaseArticleService.listPublished()

    } catch (error) {
        console.error('获取文章列表失败:', error)
        return []
    }
}

/**
 * 获取文章列表（分片版本） - 优先使用分片，后台同步本地数据库
 * @param {Object} options 选项 {status, limit, offset}
 * @returns {Promise<Array>} 文章列表
 */
export async function getArticlesFromShards(options = {}) {
    try {
        // 分片已废弃，统一从 Supabase 获取公开文章
        const { limit, offset = 0, categoryId, tagId } = options
        return await supabaseArticleService.listPublished({ limit, offset, categoryId, tagId })

    } catch (error) {
        console.error('从分片获取文章列表失败:', error)
        return []
    }
}

/**
 * 获取博客配置
 * @returns {Promise<Object>} 博客配置
 */
export async function getBlogConfig() {
    try {
        // 从meta.json获取博客配置
        const url = 'https://raw.githubusercontent.com/mizorehe70dayo/MahiruBlogWeb/main/public/data/meta.json'
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()

        // 返回配置信息，包含统计数据
        return {
            stats: data.stats || {},
            version: data.version,
            last_updated: data.last_updated,
            // 可以添加其他博客配置
            title: "MAHIRU的笔记",
            subtitle: "记录生活中的美好瞬间"
        }
    } catch (error) {
        console.error('获取博客配置失败:', error)
        // 返回默认配置
        return {
            stats: {
                total_articles: 0,
                published_articles: 0,
                total_categories: 0,
                total_tags: 0
            },
            title: "MAHIRU的笔记",
            subtitle: "记录生活中的美好瞬间"
        }
    }
}

/**
 * 根据ID获取文章 - 集成本地数据库
 * @param {string} id - 文章ID
 * @returns {Promise<Object|null>} 文章对象
 */
export async function getArticleById(id) {
    try {
        const numericId = typeof id === 'string' ? parseInt(id, 10) : id
        return await supabaseArticleService.getPublicById(numericId)
    } catch (error) {
        console.error('获取文章失败:', error)
        return null
    }
}

/**
 * 根据ID获取文章 - 管理端专用（无访问限制）
 * @param {string} id - 文章ID
 * @returns {Promise<Object|null>} 文章对象
 */
export async function getArticleByIdForAdmin(id) {
    try {
        const numericId = typeof id === 'string' ? parseInt(id, 10) : id
        return await supabaseArticleService.getByIdForAdmin(numericId)
    } catch (error) {
        console.error('获取文章失败:', error)
        return null
    }
}

// 文章内容由 Supabase 返回 content 或 Storage URL 加载，不再依赖 GitHub Raw

/**
 * 根据标签筛选文章 - 使用本地数据库
 * @param {string} tagId - 标签ID
 * @returns {Promise<Array>} 筛选后的文章列表
 */
export async function getArticlesByTag(tagId) {
    try {
        // 公开文章按标签筛选
        return await supabaseArticleService.listPublished({ tagId })
    } catch (error) {
        console.error('根据标签筛选文章失败:', error)
        return []
    }
}

/**
 * 根据分类筛选文章 - 使用本地数据库
 * @param {number} categoryId - 分类ID
 * @returns {Promise<Array>} 筛选后的文章列表
 */
export async function getArticlesByCategory(categoryId) {
    try {
        return await supabaseArticleService.listPublished({ categoryId })
    } catch (error) {
        console.error('根据分类筛选文章失败:', error)
        return []
    }
}

/**
 * 搜索文章 - 使用本地数据库
 * @param {string} keyword - 搜索关键词
 * @returns {Promise<Array>} 搜索结果
 */
export async function searchArticles(keyword) {
    try {
        // 简单搜索：客户端过滤（可改为 Postgres 全文检索）
        const all = await supabaseArticleService.listPublished()
        const q = (keyword || '').toLowerCase()
        return all.filter(a => (a.title || '').toLowerCase().includes(q) || (a.excerpt || '').toLowerCase().includes(q))
    } catch (error) {
        console.error('搜索文章失败:', error)
        return []
    }
}

/**
 * 获取所有标签 - 使用本地数据库
 * @returns {Promise<Array>} 标签列表
 */
export async function getAllTags() {
    try {
        // 直接从 Supabase 云端获取
        const tags = await supabaseTagService.list()
        return tags
    } catch (error) {
        console.error('获取标签列表失败:', error)
        return []
    }
}

/**
 * 获取所有分类 - 使用本地数据库
 * @returns {Promise<Array>} 分类列表
 */
export async function getAllCategories() {
    try {
        // 直接从 Supabase 云端获取
        const categories = await supabaseCategoryService.list()
        return categories
    } catch (error) {
        console.error('获取分类列表失败:', error)
        return []
    }
}

/**
 * 获取最新文章 - 使用本地数据库
 * @param {number} limit - 限制数量
 * @returns {Promise<Array>} 最新文章列表
 */
export async function getLatestArticles(limit = 5) {
    try {
        const all = await supabaseArticleService.listPublished({ limit })
        return all
    } catch (error) {
        console.error('获取最新文章失败:', error)
        return []
    }
}

/**
 * 获取热门文章 - 使用本地数据库
 * @param {number} limit - 限制数量
 * @returns {Promise<Array>} 热门文章列表
 */
export async function getPopularArticles(limit = 5) {
    try {
        // 暂无热度指标：以发布时间排序取前 N
        const all = await supabaseArticleService.listPublished({ limit })
        return all
    } catch (error) {
        console.error('获取热门文章失败:', error)
        return []
    }
}

/**
 * 获取数据库统计信息
 * @returns {Promise<Object>} 统计信息
 */
export async function getStats() {
    try {
        const { getAllArticles, getAllTags } = await import('./dataService.js')
        const supabaseArticleService = (await import('./supabaseArticleService.js')).default
        const supabaseTagService = (await import('./supabaseTagService.js')).default

        // 并行获取统计数据
        const [articleStats, tagStats] = await Promise.all([
            supabaseArticleService.getStats(),
            supabaseTagService.getStats()
        ])

        return {
            articleCount: articleStats.articleCount,
            tagCount: tagStats.tagCount,
            totalWords: articleStats.totalWords
        }
    } catch (error) {
        console.error('获取统计信息失败:', error)
        return {
            articleCount: 0,
            tagCount: 0,
            totalWords: 0
        }
    }
}

/**
 * 强制同步数据
 * @returns {Promise<Object>} 同步结果
 */
export async function forceSyncData() {
    try {
        // 旧的同步检查已由metaCacheService替代
        return { success: true, message: '同步成功' }
    } catch (error) {
        console.error('强制同步失败:', error)
        return { success: false, message: '同步失败' }
    }
}

/**
 * 获取同步状态
 * @returns {Object} 同步状态
 */
export function getSyncStatus() {
    // 旧的同步检查已由metaCacheService替代
    return { status: 'idle', lastSync: null }
}

/**
 * 初始化数据服务
 * @returns {Promise<boolean>} 初始化结果
 */
export async function initDataService() {
    try {
        // 旧的同步检查已由metaCacheService替代

        return true
    } catch (error) {
        console.error('数据服务初始化失败:', error)
        return false
    }
}

// ==================== 降级方案 ====================

// 已废弃：GitHub JSON 分片/云端降级

// ==================== 管理功能导出 ====================

// 旧的标签管理导出已移除，标签 CRUD 改为使用 Supabase 服务（supabaseTagService）

// 旧的分类管理导出已移除，分类 CRUD 改为使用 Supabase 服务（supabaseCategoryService） 